// AutoSave — debounced save with state machine, visibility hook, dan manual flush.
// Subscribe ke ProjectStore; debounce perubahan selama `debounceMs`; panggil `saveFn`.
// State machine: IDLE → DIRTY → SAVING → SAVED | ERROR.
// Hook `visibilitychange` + `pagehide` untuk flush save sebelum tab hidden.

import { showToast } from '@tm/ui-kit'
import { STRINGS } from '@tm/shared'

const STATES = Object.freeze({
  IDLE: 'idle',
  DIRTY: 'dirty',
  SAVING: 'saving',
  SAVED: 'saved',
  ERROR: 'error',
})

export const AUTO_SAVE_STATES = STATES

const DEFAULT_DEBOUNCE_MS = 2000

export function createAutoSave({
  store,
  saveFn,
  debounceMs = DEFAULT_DEBOUNCE_MS,
  onStateChange = () => {},
  toast = showToast,
  document: doc = typeof document !== 'undefined' ? document : null,
} = {}) {
  if (!store) throw new Error('AutoSave: store is required')
  if (typeof saveFn !== 'function') throw new Error('AutoSave: saveFn must be a function')

  let timer = null
  let state = STATES.IDLE
  let lastError = null
  let lastSavedAt = null
  let savingPromise = null
  let visibilityHandler = null
  let pagehideHandler = null

  function setState(next, payload = {}) {
    state = next
    onStateChange({ state, lastError, lastSavedAt, ...payload })
  }

  async function performSave() {
    if (typeof store.getProject !== 'function') {
      const err = new Error('AutoSave: store.getProject is not a function')
      lastError = err
      setState(STATES.ERROR, { error: err })
      toast(STRINGS.autoSaveError, { type: 'warn', durationMs: 3000 })
      return
    }
    setState(STATES.SAVING)
    try {
      const project = store.getProject()
      await saveFn(project)
      lastError = null
      lastSavedAt = Date.now()
      setState(STATES.SAVED, { lastSavedAt })
      toast(STRINGS.toastSaved, { type: 'success', durationMs: 1500 })
    } catch (err) {
      lastError = err
      setState(STATES.ERROR, { error: err })
      toast(STRINGS.autoSaveError, { type: 'warn', durationMs: 3000 })
      // eslint-disable-next-line no-console
      console.error('[AutoSave] failed:', err)
    }
  }

  function schedule() {
    if (timer !== null) clearTimeout(timer)
    setState(STATES.DIRTY)
    timer = setTimeout(() => {
      timer = null
      savingPromise = performSave()
    }, debounceMs)
  }

  function flush() {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (savingPromise) return savingPromise
    savingPromise = performSave()
    return savingPromise
  }

  function save() {
    return flush()
  }

  function disable() {
    if (timer !== null) {
      clearTimeout(timer)
      timer = null
    }
    if (typeof unsubscribe === 'function') {
      unsubscribe()
      unsubscribe = null
    }
    removeLifecycleHooks()
    setState(STATES.IDLE)
  }

  function removeLifecycleHooks() {
    if (doc && visibilityHandler) {
      doc.removeEventListener('visibilitychange', visibilityHandler)
      visibilityHandler = null
    }
    if (typeof window !== 'undefined' && pagehideHandler) {
      window.removeEventListener('pagehide', pagehideHandler)
      pagehideHandler = null
    }
  }

  // Subscribe to store — setiap perubahan schedule debounced save.
  let unsubscribe = store.subscribe(() => {
    // Skip UI-only events (sudah di-handle ProjectStore), schedule tetap fire untuk semua non-UI change.
    schedule()
  })

  // Hook visibilitychange + pagehide untuk flush sebelum tab hidden.
  if (doc) {
    visibilityHandler = () => {
      if (doc.visibilityState === 'hidden') flush()
    }
    doc.addEventListener('visibilitychange', visibilityHandler)
  }
  if (typeof window !== 'undefined') {
    pagehideHandler = () => flush()
    window.addEventListener('pagehide', pagehideHandler)
  }

  return {
    flush,
    save,
    disable,
    getState: () => ({ state, lastError, lastSavedAt }),
  }
}
